import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useGetRetailerByIdQuery } from "../../../services/retailers/retailersApiSlice";
import { useUpdateRetailerListMutation } from "../../../services/retailers/retailerMutationApiSlice";

import classes from "./retailerDetailPage.module.css";
import { ImagePreview } from "../../../component/retailer/imagePreview/ImagePreview";

import { FormInput } from "../../../component/retailer/formInput/FormInput";
import { FormCheckboxChips } from "../../../component/retailer/formCheckBoxChip/FormCheckBoxChip";

export const RetailerDetailPage = () => {
  const { retailerId } = useParams();

  // Fetch retailer data
  const { data, isLoading } = useGetRetailerByIdQuery(retailerId);

  // Mutation for updating retailer
  const [updateRetailer, { isLoading: isUpdating }] =
    useUpdateRetailerListMutation();

  // Store initial values to compare changes
  const initialValuesRef = useRef(null);

  // React Hook Form
  const { register, setValue, handleSubmit, watch } = useForm({
    defaultValues: {
      name: "",
      email_address: "",
      phone_number: "",
      gst_number: "",
      pan_number: "",
      aadhar_number: "",
      p_status_id: "",
      pan_image_url: "",
      aadhar_image_url: "",
    },
  });

  const status = watch("p_status_id");

  const verificationOptions = [
    {
      id: 3,
      label: status === 3 || status === "3" ? "Verified" : "Verify",
    },
    {
      id: 4,
      label: status === 4 || status === "4" ? "Rejected" : "Reject",
    },
  ];

  // Fill form and store initial values when data loads
  useEffect(() => {
    if (!data) return;

    const formFields = [
      "name",
      "email",
      "mobile_no",
      "gst_number",
      "pan_number",
      "aadhar_number",
    ];

    formFields.forEach((field) => setValue(field, data[field] || ""));

    const statusValue = [0, 1, 2].includes(data.p_status_id)
      ? ""
      : data.p_status_id;
    setValue("p_status_id", statusValue);

    setValue("pan_image_url", data.pan_image_url || "");
    setValue("aadhar_image_url", data.aadhar_image_url || "");

    initialValuesRef.current = {
      p_status_id: statusValue,
      pan_image_url: data.pan_image_url || "",
      aadhar_image_url: data.aadhar_image_url || "",
    };
  }, [data, setValue]);

  const onSubmit = async (values) => {
    const initial = initialValuesRef.current;

    // Check if anything changed
    if (
      initial.p_status_id === Number(values.p_status_id) &&
      initial.pan_image_url === values.pan_image_url &&
      initial.aadhar_image_url === values.aadhar_image_url
    ) {
      toast.info("No changes made.");
      return;
    }

    try {
      await updateRetailer({
        id: retailerId,
        status: Number(values.p_status_id),
        pan_image_url: values.pan_image_url,
        aadhar_image_url: values.aadhar_image_url,
      }).unwrap();
      console.log(values);

      toast.success("Retailer updated successfully!");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update retailer.");
    }
  };

  if (isLoading)
    return <div className={classes.loading}>Loading retailer details...</div>;

  return (
    <div className={classes.wrapper}>
      <h2 className={classes.pageTitle}>Retailer Details</h2>

      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        {/* --- BASIC INFO --- */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Basic Information</h3>
          <div className={classes.grid}>
            <FormInput
              id="name"
              label="Name"
              type="text"
              disabled
              register={register}
            />
            <FormInput
              id="email"
              label="Email Address"
              type="email"
              disabled
              register={register}
            />
            <FormInput
              id="mobile_no"
              label="Phone Number"
              type="number"
              disabled
              register={register}
            />
            <FormInput
              id="gst_number"
              label="GST Number"
              type="text"
              disabled
              register={register}
            />
            <FormInput
              id="pan_number"
              label="PAN Number"
              type="text"
              disabled
              register={register}
            />
            <FormInput
              id="aadhar_number"
              label="Aadhar Number"
              type="text"
              disabled
              register={register}
            />
          </div>
        </div>

        {/* --- STATUS --- */}

        {/* --- DOCUMENTS --- */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Documents</h3>
          <div className={classes.documentsGrid}>
            <ImagePreview url={data?.pan_image_url} label="PAN Image" />
            <ImagePreview url={data?.aadhar_image_url} label="Aadhar Image" />
          </div>
        </div>
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Verification Status</h3>
          <div className={classes.singleField}>
            <FormCheckboxChips
              id="p_status_id"
              options={verificationOptions}
              register={register}
              watch={watch}
              setValue={setValue}
            />
          </div>
        </div>

        {/* --- SUBMIT BUTTON --- */}
        <div className={classes.actions}>
          <button
            className={classes.submitBtn}
            type="submit"
            disabled={isUpdating}
          >
            {isUpdating ? "Updating..." : "Update Status"}
          </button>
        </div>
      </form>
    </div>
  );
};
